import { config } from './config.js';
import { apiClient } from './apiClient.js';
import { initSentry, captureError, setUser as setSentryUser } from './sentry.js';
import { initAuth, getUser, signIn, signOut, onAuthChange, isConfigured as isAuthConfigured } from './auth.js';

const DB_NAME = config.dbName;
const DB_VERSION = config.dbVersion;
const STORE = config.storeName;
const FREE_STORAGE_BYTES = config.freeStorageBytes;
const TRIAL_DAYS = config.trialDays;

const prompts = {
  person: [
    'Tell me about someone important to you.',
    'What is one detail about them that only close people would know?',
    'Is there a story that captures who they really are?',
    'What did they teach you by how they lived?',
  ],
  place: [
    "Tell me about a place that's shaped you.",
    'What does it sound like, smell like, or feel like there?',
    'Who else belongs in that place when you picture it?',
    'What would a stranger miss if you took them there?',
  ],
  skill: [
    "What's something you know how to do that's hard to explain?",
    'What do beginners usually get wrong?',
    'How do you know when you are doing it well?',
    'Who taught you, or how did you figure it out?',
  ],
  freeform: [
    "I'm listening. Start wherever feels natural.",
    'Say more about that.',
    'What happened next?',
    'What does that remind you of?',
  ],
};

const state = {
  db: null,
  captures: [],
  filter: 'all',
  mode: null,
  topic: 'freeform',
  promptIndex: 0,
  recorder: null,
  stream: null,
  chunks: [],
  audioBlob: null,
  audioContext: null,
  analyser: null,
  meterId: null,
  recognition: null,
  installEvent: null,
  planStatus: 'trial_active',
  capabilities: {},
  libTab: 'organized',
  folderView: 'root',
  sessionsView: 'list',
  folders: [],
  currentFolder: null,
  currentDocs: [],
  currentDoc: null,
  currentCapture: null,
  settings: {
    displayName: '',
    browserSpeech: true,
  },
};

const FOLDER_TONES = ['purple', 'amber', 'green', 'blue', 'rose', 'gold'];

const $ = (id) => document.getElementById(id);

window.addEventListener('DOMContentLoaded', init);

async function init() {
  await initSentry();
  buildWave();
  bindUi();
  state.settings = { ...state.settings, ...loadSettings() };
  applyPlanState();
  applySettings();
  try {
    state.db = await openDb();
    await loadCaptures();
  } catch (e) {
    captureError(e, { stage: 'openDb' });
    toast('Local storage unavailable — captures will not persist.');
  }
  registerServiceWorker();
  setupInstallPrompt();
  setupSpeechRecognition();
  if (new URLSearchParams(location.search).has('capture')) switchTab('capture');
  await bootAuth();
  await loadFolders();
  renderLibrary();
}

async function bootAuth() {
  if (!isAuthConfigured()) {
    applyAuthState();
    return;
  }
  try {
    await initAuth();
    onAuthChange(() => {
      applyAuthState();
      loadFolders().then(() => renderLibrary()).catch((e) => captureError(e, { stage: 'loadFolders' }));
    });
    applyAuthState();
  } catch (e) {
    captureError(e, { stage: 'initAuth' });
  }
}

function applyAuthState() {
  const user = getUser();
  setSentryUser(user);
  const btn = document.getElementById('authBtn');
  if (!btn) return;
  if (!isAuthConfigured()) {
    btn.style.display = 'none';
    return;
  }
  btn.style.display = 'inline-flex';
  if (user) {
    btn.innerHTML = '<i class="fa-solid fa-right-from-bracket"></i>';
    btn.title = `Sign out (${user.primaryEmailAddress?.emailAddress || 'signed in'})`;
    btn.onclick = () => signOut();
  } else {
    btn.innerHTML = '<i class="fa-solid fa-right-to-bracket"></i>';
    btn.title = 'Sign in';
    btn.onclick = () => signIn();
  }
}

function bindUi() {
  document.querySelectorAll('[data-tab]').forEach((btn) => btn.addEventListener('click', () => switchTab(btn.dataset.tab)));
  document.querySelectorAll('[data-mode]').forEach((btn) => btn.addEventListener('click', () => pickMode(btn.dataset.mode)));
  document.querySelectorAll('[data-topic]').forEach((btn) => btn.addEventListener('click', () => startSession('guided', btn.dataset.topic)));
  document.querySelectorAll('[data-back]').forEach((btn) => btn.addEventListener('click', () => showScreen(btn.dataset.back)));
  document.querySelectorAll('[data-libtab]').forEach((btn) => btn.addEventListener('click', () => setLibTab(btn.dataset.libtab)));
  $('crumbHome').addEventListener('click', (e) => { e.preventDefault(); closeFolder(); });
  $('micBtn').addEventListener('click', toggleRecording);
  $('nextPromptBtn').addEventListener('click', nextPrompt);
  $('saveSessionBtn').addEventListener('click', saveSession);
  $('cancelSessionBtn').addEventListener('click', resetCapture);
  $('settingsBtn').addEventListener('click', openSettings);
  $('closeSettings').addEventListener('click', closeSettings);
  $('saveSettings').addEventListener('click', saveSettings);
  $('searchBtn').addEventListener('click', openSearch);
  $('searchOverlay').addEventListener('click', (e) => { if (e.target.id === 'searchOverlay') closeSearch(); });
  $('searchInput').addEventListener('input', runSearch);
  $('captureCrumbHome').addEventListener('click', (e) => { e.preventDefault(); closeCapture(); });
  $('docCrumbHome').addEventListener('click', (e) => { e.preventDefault(); closeDoc(true); });
  $('docCrumbFolder').addEventListener('click', (e) => { e.preventDefault(); closeDoc(false); });
  $('exportBtn').addEventListener('click', exportCaptures);
  $('installBtn').addEventListener('click', installApp);
  $('offloadInfo').addEventListener('click', () => {
    toast('Offload pushes selected captures to your desktop on the next desktop sync.');
  });
}

function switchTab(tab) {
  document.querySelectorAll('.tabs button').forEach((btn) => btn.classList.toggle('active', btn.dataset.tab === tab));
  $('capturePanel').classList.toggle('active', tab === 'capture');
  $('libraryPanel').classList.toggle('active', tab === 'library');
  document.querySelector('.library-only').style.display = tab === 'library' ? 'grid' : 'none';
}

function showScreen(id) {
  document.querySelectorAll('.screen').forEach((screen) => screen.classList.toggle('active', screen.id === id));
}

function pickMode(mode) {
  if (!state.capabilities.canCaptureWeb) {
    toast('Your trial has ended. Offload or export your captures anytime, or subscribe to keep using Sayermore Cloud.');
    switchTab('library');
    return;
  }
  state.mode = mode;
  if (mode === 'guided') showScreen('topicScreen');
  else startSession('freeform', 'freeform');
}

function startSession(mode, topic) {
  state.mode = mode;
  state.topic = topic;
  state.promptIndex = 0;
  state.audioBlob = null;
  state.chunks = [];
  $('noteText').value = '';
  setPrompt(prompts[topic][0]);
  setStatus('Tap to speak');
  showScreen('sessionScreen');
}

function nextPrompt() {
  const list = prompts[state.topic] || prompts.freeform;
  state.promptIndex = (state.promptIndex + 1) % list.length;
  setPrompt(list[state.promptIndex]);
}

function setPrompt(text) {
  $('promptText').textContent = text;
}

async function toggleRecording() {
  if (state.recorder && state.recorder.state === 'recording') {
    state.recorder.stop();
    return;
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    state.stream = stream;
    state.chunks = [];
    state.recorder = new MediaRecorder(stream);
    state.recorder.addEventListener('dataavailable', (e) => {
      if (e.data && e.data.size) state.chunks.push(e.data);
    });
    state.recorder.addEventListener('stop', finishRecording);
    state.recorder.start();
    startMeter(stream);
    startSpeech();
    $('micBtn').classList.add('recording');
    setStatus('Recording', 'rec');
  } catch (e) {
    toast(e.name === 'NotAllowedError' ? 'Microphone permission was denied.' : 'Microphone unavailable.');
  }
}

function finishRecording() {
  state.audioBlob = new Blob(state.chunks, { type: state.recorder.mimeType || 'audio/webm' });
  stopMeter();
  stopSpeech();
  if (state.stream) state.stream.getTracks().forEach((track) => track.stop());
  $('micBtn').classList.remove('recording');
  setStatus('Recording ready');
}

function startMeter(stream) {
  stopMeter();
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return;
  state.audioContext = new AudioCtx();
  state.analyser = state.audioContext.createAnalyser();
  const source = state.audioContext.createMediaStreamSource(stream);
  source.connect(state.analyser);
  state.analyser.fftSize = 256;
  const data = new Uint8Array(state.analyser.frequencyBinCount);
  const bars = [...document.querySelectorAll('.bar')];
  const tick = () => {
    state.analyser.getByteFrequencyData(data);
    const avg = data.reduce((sum, val) => sum + val, 0) / data.length;
    const pct = Math.min(100, Math.round((avg / 140) * 100));
    $('meterFill').style.width = `${pct}%`;
    bars.forEach((bar, idx) => {
      const val = data[(idx * 3) % data.length] || avg;
      bar.style.height = `${Math.max(4, Math.round((val / 255) * 24))}px`;
      bar.style.opacity = '.9';
    });
    state.meterId = requestAnimationFrame(tick);
  };
  tick();
}

function stopMeter() {
  if (state.meterId) cancelAnimationFrame(state.meterId);
  if (state.audioContext) state.audioContext.close().catch(() => {});
  state.meterId = null;
  state.audioContext = null;
  $('meterFill').style.width = '0';
  document.querySelectorAll('.bar').forEach((bar) => {
    bar.style.height = '5px';
    bar.style.opacity = '.28';
  });
}

function setupSpeechRecognition() {
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SpeechRecognition) return;
  state.recognition = new SpeechRecognition();
  state.recognition.continuous = true;
  state.recognition.interimResults = true;
  state.recognition.lang = 'en-US';
  let committed = '';
  state.recognition.addEventListener('result', (event) => {
    let interim = '';
    for (let i = event.resultIndex; i < event.results.length; i += 1) {
      const text = event.results[i][0].transcript;
      if (event.results[i].isFinal) committed += `${text.trim()} `;
      else interim += text;
    }
    const base = $('noteText').dataset.base || $('noteText').value;
    $('noteText').value = `${base}${committed}${interim}`.trimStart();
  });
  state.recognition.addEventListener('end', () => {
    $('noteText').dataset.base = $('noteText').value ? `${$('noteText').value.trim()} ` : '';
    committed = '';
  });
}

function startSpeech() {
  if (!state.settings.browserSpeech || !state.recognition) return;
  $('noteText').dataset.base = $('noteText').value ? `${$('noteText').value.trim()} ` : '';
  try { state.recognition.start(); } catch {}
}

function stopSpeech() {
  if (!state.recognition) return;
  try { state.recognition.stop(); } catch {}
}

async function saveSession() {
  if (!state.capabilities.canCaptureWeb) {
    toast('Your trial has ended. Offload or export your captures anytime.');
    switchTab('library');
    return;
  }
  if (state.recorder && state.recorder.state === 'recording') {
    state.recorder.stop();
    await delay(250);
  }
  const text = $('noteText').value.trim();
  if (!text && !state.audioBlob) {
    toast('Record audio or add notes first.');
    return;
  }
  const capture = {
    id: crypto.randomUUID(),
    mode: state.mode || 'freeform',
    topic: state.topic || 'freeform',
    prompt: $('promptText').textContent,
    title: makeTitle(text, state.topic),
    text,
    audio: state.audioBlob,
    audioType: state.audioBlob ? state.audioBlob.type : '',
    sync: false,
    createdAt: new Date().toISOString(),
  };
  const projected = estimateCaptureBytes(capture) + state.captures.reduce((sum, item) => sum + estimateCaptureBytes(item), 0);
  if (projected > FREE_STORAGE_BYTES) {
    toast('Web storage limit reached. Export or connect Cloud.');
    return;
  }
  await putCapture(capture);
  await loadCaptures();
  renderLibrary();
  resetCapture();
  switchTab('library');
  toast('Capture saved');
}

function resetCapture() {
  if (state.recorder && state.recorder.state === 'recording') state.recorder.stop();
  if (state.stream) state.stream.getTracks().forEach((track) => track.stop());
  stopMeter();
  stopSpeech();
  state.audioBlob = null;
  state.chunks = [];
  $('noteText').value = '';
  $('micBtn').classList.remove('recording');
  setStatus('Tap to speak');
  showScreen('modeScreen');
}

function renderLibrary() {
  const folderCount = state.folders.length;
  const captureCount = state.captures.length;
  const parts = [];
  if (folderCount) parts.push(`${folderCount} folder${folderCount === 1 ? '' : 's'}`);
  parts.push(`${captureCount} capture${captureCount === 1 ? '' : 's'}`);
  $('libraryStats').textContent = parts.join(' · ');
  renderStorage();
  applyLibTab();
  if (state.libTab === 'organized') {
    if (state.folderView === 'doc') renderDocView();
    else if (state.folderView === 'folder') renderFolderView();
    else renderRoot();
  } else if (state.sessionsView === 'detail') {
    renderCaptureDetail();
  } else {
    renderSessions();
  }
}

function applyLibTab() {
  document.querySelectorAll('[data-libtab]').forEach((btn) => btn.classList.toggle('active', btn.dataset.libtab === state.libTab));
  const organized = state.libTab === 'organized';
  const sessions = state.libTab === 'sessions';
  $('libTabs').style.display = (organized && state.folderView !== 'root') || (sessions && state.sessionsView !== 'list') ? 'none' : 'flex';
  $('organizedView').classList.toggle('active', organized && state.folderView === 'root');
  $('folderView').classList.toggle('active', organized && state.folderView === 'folder');
  $('docView').classList.toggle('active', organized && state.folderView === 'doc');
  $('sessionsView').classList.toggle('active', sessions && state.sessionsView === 'list');
  $('captureDetailView').classList.toggle('active', sessions && state.sessionsView === 'detail');
}

function setLibTab(tab) {
  state.libTab = tab;
  state.folderView = 'root';
  state.sessionsView = 'list';
  state.currentFolder = null;
  state.currentDoc = null;
  state.currentCapture = null;
  renderLibrary();
}

async function loadFolders() {
  try {
    state.folders = await apiClient.listFolders();
  } catch (e) {
    captureError(e, { stage: 'loadFolders' });
    state.folders = [];
  }
}

function renderRoot() {
  const list = state.folders;
  $('folderEmpty').style.display = list.length ? 'none' : 'block';
  $('folderList').innerHTML = list.map(folderCard).join('');
  document.querySelectorAll('.folder-item').forEach((item) => item.addEventListener('click', () => openFolder(item.dataset.key)));
}

function folderCard(f, i) {
  const tone = FOLDER_TONES[i % FOLDER_TONES.length];
  const count = f.docCount || 0;
  return `<button class="folder-item" data-key="${escapeAttr(f.key)}">
    <span class="folder-icon ${tone}"><i class="fa-solid fa-folder"></i></span>
    <span class="folder-info">
      <span class="folder-name">${escapeHtml(f.title || f.key)}</span>
      <span class="folder-meta">${escapeHtml(f.description || '')}</span>
    </span>
    <span class="folder-count">${count} doc${count === 1 ? '' : 's'}</span>
    <span class="folder-arrow"><i class="fa-solid fa-chevron-right"></i></span>
  </button>`;
}

async function openFolder(key) {
  state.folderView = 'folder';
  try {
    const result = await apiClient.listDocsInFolder(key);
    state.currentFolder = result.folder || { key, title: key, description: '' };
    state.currentDocs = result.docs || [];
  } catch (e) {
    captureError(e, { stage: 'openFolder', key });
    state.currentFolder = { key, title: key, description: '' };
    state.currentDocs = [];
  }
  renderLibrary();
}

function renderFolderView() {
  const folder = state.currentFolder || { key: '', title: '' };
  $('crumbFolder').textContent = folder.title || folder.key;
  $('folderTitle').textContent = folder.title || folder.key;
  $('docList').innerHTML = state.currentDocs.map(docCard).join('') || '<div class="empty empty-inline"><p>This folder is empty.</p></div>';
  document.querySelectorAll('.doc-item').forEach((item) => item.addEventListener('click', () => openDoc(folder.key, item.dataset.key)));
}

async function openDoc(folderKey, docKey) {
  state.folderView = 'doc';
  try {
    const doc = await apiClient.getDoc(folderKey, docKey);
    state.currentDoc = doc || { key: docKey, title: docKey, body: '', meta: '' };
  } catch (e) {
    captureError(e, { stage: 'openDoc', folderKey, docKey });
    state.currentDoc = { key: docKey, title: docKey, body: '', meta: '' };
  }
  renderLibrary();
}

function renderDocView() {
  const folder = state.currentFolder || { key: '', title: '' };
  const doc = state.currentDoc || { key: '', title: '', body: '', meta: '' };
  $('docCrumbFolder').textContent = folder.title || folder.key;
  $('docCrumbDoc').textContent = doc.title || doc.key;
  $('docTitleBig').textContent = doc.title || doc.key;
  $('docMetaBig').textContent = doc.meta || '';
  $('docBody').innerHTML = doc.body || '<p class="empty-inline">No content yet.</p>';
}

function closeDoc(toRoot) {
  state.currentDoc = null;
  if (toRoot) {
    state.folderView = 'root';
    state.currentFolder = null;
    state.currentDocs = [];
  } else {
    state.folderView = 'folder';
  }
  renderLibrary();
}

function docCard(d) {
  return `<button class="doc-item" data-key="${escapeAttr(d.key)}">
    <div class="doc-head">
      <span class="doc-icon"><i class="fa-solid fa-file-lines"></i></span>
      <span class="doc-title">${escapeHtml(d.title || d.key)}</span>
      <span class="doc-updated">${escapeHtml(d.updated || '')}</span>
    </div>
    <div class="doc-preview">${escapeHtml(d.preview || '')}</div>
  </button>`;
}

function closeFolder() {
  state.folderView = 'root';
  state.currentFolder = null;
  state.currentDocs = [];
  renderLibrary();
}

function renderSessions() {
  $('emptyState').style.display = state.captures.length ? 'none' : 'block';
  $('captureList').innerHTML = state.captures.map(captureCard).join('');
  document.querySelectorAll('.capture-item').forEach((item) => item.addEventListener('click', (event) => {
    const syncTarget = event.target.closest('[data-sync-id]');
    if (syncTarget) {
      event.stopPropagation();
      toggleCaptureSync(syncTarget.dataset.syncId);
      return;
    }
    openCapture(item.dataset.id);
  }));
}

function openCapture(id) {
  const c = state.captures.find((item) => item.id === id);
  if (!c) return;
  state.libTab = 'sessions';
  state.sessionsView = 'detail';
  state.currentCapture = c;
  renderLibrary();
}

function closeCapture() {
  state.sessionsView = 'list';
  state.currentCapture = null;
  renderLibrary();
}

function renderCaptureDetail() {
  const c = state.currentCapture;
  if (!c) { closeCapture(); return; }
  $('captureCrumbTitle').textContent = c.title;
  $('captureTitle').textContent = c.title;
  $('captureMeta').innerHTML = `${escapeHtml(labelFor(c.topic))} <span>&middot;</span> ${escapeHtml(formatDate(c.createdAt))} <span>&middot;</span> ${escapeHtml(c.mode)}`;
  const audio = c.audio ? `<div class="audio-block"><audio controls src="${URL.createObjectURL(c.audio)}"></audio></div>` : '';
  $('captureBody').innerHTML = `<div class="detail-text">${escapeHtml(c.text || c.prompt || '')}</div>${audio}`;
  $('captureActions').innerHTML = `
    <button class="pill" id="syncCapture"><i class="fa-solid fa-computer"></i> ${c.sync ? 'Remove offload' : 'Offload'}</button>
    <button class="pill" id="copyCapture"><i class="fa-solid fa-copy"></i> Copy</button>
    <button class="pill danger" id="deleteCapture"><i class="fa-solid fa-trash"></i> Delete</button>`;
  $('syncCapture').addEventListener('click', () => toggleCaptureSync(c.id, true));
  $('copyCapture').addEventListener('click', () => copyCapture(c));
  $('deleteCapture').addEventListener('click', () => deleteCapture(c.id));
}

function renderStorage() {
  const used = state.captures.reduce((sum, item) => sum + estimateCaptureBytes(item), 0);
  const synced = state.captures.filter((item) => item.sync).reduce((sum, item) => sum + estimateCaptureBytes(item), 0);
  const pct = Math.min(100, (used / FREE_STORAGE_BYTES) * 100);
  $('storageFill').style.width = `${pct}%`;
  $('storageText').textContent = `${formatBytes(used)} of 1 GB local`;
  $('planText').textContent = planLabel(state.planStatus);
  $('syncText').textContent = `${formatBytes(synced)} selected`;
  $('syncStatusText').textContent = synced ? 'Queued for desktop' : 'Nothing queued';
  $('storageHint').textContent = 'Offload captures you want pushed to your local desktop. The next time your desktop app opens and syncs, it saves them locally and removes them from the web app.';
}

function captureCard(c) {
  const tone = c.topic === 'place' ? 'blue' : c.topic === 'skill' ? 'amber' : c.topic === 'freeform' ? 'green' : 'purple';
  const icon = c.topic === 'place' ? 'fa-location-dot' : c.topic === 'skill' ? 'fa-hammer' : c.topic === 'freeform' ? 'fa-road' : 'fa-user';
  const preview = c.text || (c.audio ? 'Audio capture' : 'Untitled capture');
  return `<button class="capture-item" data-id="${escapeAttr(c.id)}">
    <div class="capture-head">
      <span class="capture-type ${tone}"><i class="fa-solid ${icon}"></i></span>
      <span class="capture-title">${escapeHtml(c.title)}</span>
      <span class="sync-chip ${c.sync ? 'active' : ''}" data-sync-id="${escapeAttr(c.id)}"><i class="fa-solid ${c.sync ? 'fa-rotate' : 'fa-computer'}"></i> ${c.sync ? 'Queued' : 'Offload'}</span>
      <span class="capture-date">${formatDate(c.createdAt)}</span>
    </div>
    <div class="capture-preview">${escapeHtml(preview)}</div>
  </button>`;
}

async function toggleCaptureSync(id, rerenderDetail = false) {
  if (!state.capabilities.canOffloadToDesktop) {
    toast('Sign in to offload captures to your desktop.');
    return;
  }
  const capture = state.captures.find((item) => item.id === id);
  if (!capture) return;
  const next = !capture.sync;
  capture.sync = next;
  await putCapture(capture);
  await loadCaptures();
  if (rerenderDetail && state.currentCapture && state.currentCapture.id === id) {
    state.currentCapture = capture;
  }
  renderLibrary();
  toast(next ? 'Will offload next time desktop syncs.' : 'Removed from offload.');
}

function openSearch() {
  $('searchOverlay').classList.add('open');
  $('searchInput').value = '';
  $('searchResults').innerHTML = '';
  setTimeout(() => $('searchInput').focus(), 40);
}

function closeSearch() {
  $('searchOverlay').classList.remove('open');
}

function runSearch() {
  const query = $('searchInput').value.trim().toLowerCase();
  if (!query) {
    $('searchResults').innerHTML = '';
    return;
  }
  const hits = state.captures.filter((c) => `${c.title} ${c.text} ${c.prompt}`.toLowerCase().includes(query));
  $('searchResults').innerHTML = hits.map((c) => `<div class="result" data-id="${escapeAttr(c.id)}"><strong>${escapeHtml(c.title)}</strong><br><small>${escapeHtml(formatDate(c.createdAt))}</small></div>`).join('');
  document.querySelectorAll('.result').forEach((item) => item.addEventListener('click', () => {
    closeSearch();
    switchTab('library');
    openCapture(item.dataset.id);
  }));
}

function openSettings() {
  $('displayName').value = state.settings.displayName || '';
  $('browserSpeech').checked = Boolean(state.settings.browserSpeech);
  $('settingsDrawer').classList.add('open');
}

function closeSettings() {
  $('settingsDrawer').classList.remove('open');
}

function saveSettings() {
  state.settings = {
    displayName: $('displayName').value.trim(),
    browserSpeech: $('browserSpeech').checked,
  };
  localStorage.setItem('sayermore.settings', JSON.stringify(state.settings));
  applySettings();
  closeSettings();
  toast('Settings saved');
}

function applySettings() {
  $('syncLabel').textContent = 'your knowledge layer';
  $('browserSpeech').checked = Boolean(state.settings.browserSpeech);
  renderTrialBanner();
  renderStorage();
}

function applyPlanState() {
  const started = ensureTrialStarted();
  const elapsed = Date.now() - started;
  const expired = elapsed > TRIAL_DAYS * 24 * 60 * 60 * 1000;
  state.planStatus = expired ? 'trial_expired' : 'trial_active';
  const active = state.planStatus === 'trial_active' || state.planStatus === 'paid_active';
  state.capabilities = {
    canCaptureWeb: active,
    canKeepHostedData: active,
    canUploadFromDesktop: active,
    canOffloadToDesktop: true,
    canExport: true,
  };
}

function ensureTrialStarted() {
  const key = 'sayermore.trialStartedAt';
  const existing = Number(localStorage.getItem(key) || 0);
  if (existing > 0) return existing;
  const now = Date.now();
  localStorage.setItem(key, String(now));
  return now;
}

function renderTrialBanner() {
  const banner = $('trialBanner');
  if (!banner) return;
  if (state.planStatus !== 'trial_expired') {
    banner.style.display = 'none';
    return;
  }
  banner.style.display = 'block';
  banner.textContent = 'Your trial has ended. Offload or export your captures anytime, or subscribe to keep using Sayermore Cloud.';
}

function planLabel(status) {
  return {
    trial_active: 'Trial active',
    trial_expired: 'Trial ended',
    paid_active: 'Paid active',
    paid_past_due: 'Payment issue',
    anonymous: 'Sign in required',
  }[status] || 'Trial active';
}

function loadSettings() {
  try { return JSON.parse(localStorage.getItem('sayermore.settings') || '{}'); } catch { return {}; }
}

function setupInstallPrompt() {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    state.installEvent = event;
    $('installBtn').style.display = 'grid';
  });
  if (window.matchMedia('(display-mode: standalone)').matches || navigator.standalone) {
    $('installBtn').style.display = 'none';
  }
}

async function installApp() {
  if (!state.installEvent) {
    toast('Use your browser menu to add Sayermore to the home screen.');
    return;
  }
  state.installEvent.prompt();
  await state.installEvent.userChoice.catch(() => {});
  state.installEvent = null;
}

async function exportCaptures() {
  if (!state.captures.length) {
    toast('No captures to export.');
    return;
  }
  const body = state.captures.map((c) => `# ${c.title}\n\n${formatDate(c.createdAt)} · ${labelFor(c.topic)}\n\n${c.text || c.prompt || ''}`).join('\n\n---\n\n');
  const blob = new Blob([body], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `sayermore-captures-${new Date().toISOString().slice(0, 10)}.md`;
  a.click();
  URL.revokeObjectURL(url);
}

async function copyCapture(c) {
  await navigator.clipboard.writeText(`# ${c.title}\n\n${c.text || c.prompt || ''}`).catch(() => {});
  toast('Copied');
}

async function deleteCapture(id) {
  await deleteFromDb(id);
  await loadCaptures();
  closeCapture();
  toast('Deleted');
}

function buildWave() {
  $('wave').innerHTML = Array.from({ length: 24 }, () => '<span class="bar"></span>').join('');
}

function setStatus(text, cls = '') {
  $('statusText').textContent = text;
  $('statusText').className = `status ${cls}`.trim();
}

function makeTitle(text, topic) {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (clean) return clean.split(' ').slice(0, 8).join(' ').replace(/[.,;:!?]$/, '');
  return `${labelFor(topic)} capture`;
}

function labelFor(topic) {
  return { person: 'Person', place: 'Place', skill: 'Skill', freeform: 'Free-form' }[topic] || 'Capture';
}

function estimateCaptureBytes(capture) {
  const textBytes = new Blob([JSON.stringify({
    id: capture.id,
    mode: capture.mode,
    topic: capture.topic,
    prompt: capture.prompt,
    title: capture.title,
    text: capture.text,
    createdAt: capture.createdAt,
  })]).size;
  return textBytes + (capture.audio ? capture.audio.size : 0);
}

function formatBytes(bytes) {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (bytes >= 1024 * 1024) return `${Math.max(0.1, bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.ceil(bytes / 1024)} KB`;
}

function formatDate(iso) {
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(new Date(iso));
}

function toast(message) {
  $('toast').textContent = message;
  $('toast').classList.add('show');
  clearTimeout(toast.id);
  toast.id = setTimeout(() => $('toast').classList.remove('show'), 2400);
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function escapeHtml(value) {
  return String(value || '').replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
}

function escapeAttr(value) {
  return escapeHtml(value).replace(/`/g, '&#96;');
}

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (event) => {
      const db = req.result;
      const oldV = event.oldVersion;
      if (oldV < 1) {
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' });
      }
      // Future migrations: branch on oldV here (e.g. if (oldV < 2) addIndex(...))
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('IDB upgrade blocked by another tab'));
  });
}

function tx(mode = 'readonly') {
  return state.db.transaction(STORE, mode).objectStore(STORE);
}

function putCapture(capture) {
  return new Promise((resolve, reject) => {
    let req;
    try {
      req = tx('readwrite').put(capture);
    } catch (e) {
      captureError(e, { stage: 'putCapture' });
      toast('Could not save capture.');
      reject(e);
      return;
    }
    req.onsuccess = resolve;
    req.onerror = () => {
      const err = req.error;
      if (err && err.name === 'QuotaExceededError') {
        toast('Storage limit reached. Export or offload captures to make room.');
      } else {
        captureError(err, { stage: 'putCapture' });
      }
      reject(err);
    };
  });
}

function deleteFromDb(id) {
  return new Promise((resolve, reject) => {
    const req = tx('readwrite').delete(id);
    req.onsuccess = resolve;
    req.onerror = () => reject(req.error);
  });
}

function loadCaptures() {
  return new Promise((resolve, reject) => {
    const req = tx().getAll();
    req.onsuccess = () => {
      state.captures = req.result.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      resolve();
    };
    req.onerror = () => reject(req.error);
  });
}

async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.register('sw.js');
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'SW_UPDATED') {
        toast('Update available. Reloading…');
        setTimeout(() => location.reload(), 1500);
      }
    });
    reg.addEventListener('updatefound', () => {
      const sw = reg.installing;
      if (!sw) return;
      sw.addEventListener('statechange', () => {
        if (sw.state === 'installed' && navigator.serviceWorker.controller) {
          sw.postMessage({ type: 'SKIP_WAITING' });
        }
      });
    });
    setInterval(() => { reg.update().catch(() => {}); }, 60 * 60 * 1000);
  } catch (e) {
    captureError(e, { stage: 'registerServiceWorker' });
  }
}
